import algoliasearch from 'algoliasearch';
import { FBDocTypeEssentials, NoSqlDoc } from '@rabbit/firebase/doctype';
import { GetAlgoliaCreds, GetSearchIndexName } from './config';

interface IndexCacheEntry {
  baseIndex: string;
  availableReplicas: string[];
  supportedFields: string[];
  lastUpdated: number;
  expiresAt: number;
}

interface ReplicaInfo {
  indexName: string;
  fieldName: string;
  direction: 'asc' | 'desc';
}

/**
 * Global cache for index discovery results
 * Key: baseIndexName (e.g., 'holding.eulaunchpad.v3')
 * Value: IndexCacheEntry
 */
const indexCache: Map<string, IndexCacheEntry> = new Map();

/**
 * Cache TTL in milliseconds (default: 15 minutes)
 * This ensures we don't hit Algolia API too frequently
 */
const CACHE_TTL = 24 * 60 * 60 * 1000;

/**
 * Minimal refresh interval to prevent API abuse (5 minutes)
 */
const MIN_REFRESH_INTERVAL = 24 * 60 * 60 * 1000;

/**
 * Extracts field name and direction from replica index name
 * Expected format: {baseIndex}_{fieldName}_{direction}
 * @param baseIndex - Base index name (e.g., 'holding.eulaunchpad.v3')
 * @param replicaIndex - Replica index name (e.g., 'holding.eulaunchpad.v3_warranty_startDate_desc')
 * @returns ReplicaInfo or null if parsing fails
 */
function parseReplicaIndex(baseIndex: string, replicaIndex: string): ReplicaInfo | null {
  if (!replicaIndex.startsWith(baseIndex + '_')) {
    return null;
  }
  
  const suffix = replicaIndex.substring(baseIndex.length + 1);
  const parts = suffix.split('_');
  
  if (parts.length < 2) {
    return null;
  }
  
  const direction = parts[parts.length - 1];
  if (direction !== 'asc' && direction !== 'desc') {
    return null;
  }
  
  const fieldName = parts.slice(0, -1).join('_');
  
  return {
    indexName: replicaIndex,
    fieldName,
    direction: direction as 'asc' | 'desc'
  };
}

/**
 * Discovers all replica indices for a given base index by scanning Algolia
 * This is an expensive operation that should be cached
 * @param baseIndexName - The base index name
 * @param appId - Algolia app ID
 * @param searchApiKey - Algolia search API key
 * @returns Promise<IndexCacheEntry>
 */
async function discoverIndices(
  baseIndexName: string, 
  appId: string, 
  searchApiKey: string
): Promise<IndexCacheEntry> {
  // console.log(`Discovering indices for base index: ${baseIndexName}`);
  
  const client = algoliasearch(appId, searchApiKey);
  const availableReplicas: string[] = [];
  const supportedFields: string[] = [];
  
  try {
    // List all indices in the Algolia app
    const { items: allIndices } = await client.listIndices();
    
    // Find replicas that match our base index pattern
    const replicaCandidates = allIndices
      .map(index => index.name)
      .filter(name => name.startsWith(baseIndexName + '_'));
    
    // Validate each candidate by parsing its name and checking existence
    for (const candidateIndex of replicaCandidates) {
      const replicaInfo = parseReplicaIndex(baseIndexName, candidateIndex);
      
      if (replicaInfo) {
        try {
          const index = client.initIndex(candidateIndex);
          const exists = await index.exists();
          
          if (exists) {
            availableReplicas.push(candidateIndex);
            
            // Add field to supported fields (avoid duplicates)
            if (!supportedFields.includes(replicaInfo.fieldName)) {
              supportedFields.push(replicaInfo.fieldName);
            }
          }
        } catch (error) {
          console.warn(`Failed to verify replica ${candidateIndex}:`, error);
        }
      }
    }
    
    // console.log(`Found ${availableReplicas.length} replica indices for ${baseIndexName}`);
    // console.log(`Supported sort fields: ${supportedFields.join(', ')}`);
    
  } catch (error) {
    console.error(`Failed to discover indices for ${baseIndexName}:`, error);
  }
  
  const now = Date.now();
  return {
    baseIndex: baseIndexName,
    availableReplicas,
    supportedFields,
    lastUpdated: now,
    expiresAt: now + CACHE_TTL,
  };
}

/**
 * Gets cached index information or discovers it if cache is stale
 * @param type - Document type
 * @returns Promise<IndexCacheEntry>
 */
export async function getCachedIndexInfo<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
): Promise<IndexCacheEntry> {
  const baseIndexName = GetSearchIndexName(type);
  const now = Date.now();
  
  // Check if we have valid cached data
  const cached = indexCache.get(baseIndexName);
  if (cached && cached.expiresAt > now) {
    // console.log(`Using cached index info for ${baseIndexName} (expires in ${Math.round((cached.expiresAt - now) / 1000 / 60)} minutes)`);
    return cached;
  }
  
  // Check if we recently updated (prevent API abuse)
  if (cached && (now - cached.lastUpdated) < MIN_REFRESH_INTERVAL) {
    // console.log(`Recent refresh for ${baseIndexName}, extending cache validity`);
    cached.expiresAt = now + CACHE_TTL;
    return cached;
  }
  
  // Discover indices and cache the result
  const { appId, searchApiKey } = GetAlgoliaCreds(type);
  const indexInfo = await discoverIndices(baseIndexName, appId, searchApiKey);
  
  indexCache.set(baseIndexName, indexInfo);
  return indexInfo;
}

/**
 * Manually refresh cache for a specific document type
 * Useful for testing or when you know indices have changed
 * @param type - Document type
 * @returns Promise<IndexCacheEntry>
 */
export async function refreshIndexCache<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>
): Promise<IndexCacheEntry> {
  const baseIndexName = GetSearchIndexName(type);
  // console.log(`Manually refreshing index cache for ${baseIndexName}`);
  
  const { appId, searchApiKey } = GetAlgoliaCreds(type);
  const indexInfo = await discoverIndices(baseIndexName, appId, searchApiKey);
  
  indexCache.set(baseIndexName, indexInfo);
  return indexInfo;
}

/**
 * Get all currently cached index information (useful for debugging)
 * @returns Map of all cached index info
 */
export function getAllCachedIndexInfo(): Map<string, IndexCacheEntry> {
  return new Map(indexCache);
}

/**
 * Clear all cached index information
 * Useful for testing or when you want to force fresh discovery
 */
export function clearIndexCache(): void {
  // console.log(`Clearing all index cache (${indexCache.size} entries)`);
  indexCache.clear();
}

/**
 * Check if a specific field is supported for sorting in the given index
 * @param type - Document type
 * @param fieldName - Field to check
 * @returns Promise<boolean>
 */
export async function isFieldSortable<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>,
  fieldName: string
): Promise<boolean> {
  const indexInfo = await getCachedIndexInfo(type);
  return indexInfo.supportedFields.includes(fieldName);
}

/**
 * Get the appropriate replica index name for a field and direction
 * Returns null if no replica exists for the combination
 * @param type - Document type
 * @param fieldName - Field to sort by
 * @param direction - Sort direction
 * @returns Promise<string | null>
 */
export async function getReplicaIndexName<TYPE extends NoSqlDoc>(
  type: FBDocTypeEssentials<TYPE>,
  fieldName: string,
  direction: 'asc' | 'desc'
): Promise<string | null> {
  const indexInfo = await getCachedIndexInfo(type);
  const baseIndexName = indexInfo.baseIndex;
  
  // Look for exact match first: {baseIndex}_{field}_{direction}
  const exactMatch = `${baseIndexName}_${fieldName}_${direction}`;
  if (indexInfo.availableReplicas.includes(exactMatch)) {
    return exactMatch;
  }
  
  // Look for opposite direction match: {baseIndex}_{field}_{opposite}
  const oppositeDirection = direction === 'asc' ? 'desc' : 'asc';
  const oppositeMatch = `${baseIndexName}_${fieldName}_${oppositeDirection}`;
  if (indexInfo.availableReplicas.includes(oppositeMatch)) {
    // console.log(`Using ${oppositeDirection} replica for ${direction} sort: ${oppositeMatch} (will reverse results)`);
    return oppositeMatch;
  }
  
  return null;
}

// ===========================
// PERIODIC CACHE MANAGEMENT
// ===========================

let cacheRefreshInterval: NodeJS.Timeout | null = null;

/**
 * Starts periodic cache refresh for all registered document types
 * This ensures index discovery stays current with Algolia changes
 * @param intervalMs - Refresh interval in milliseconds (default: 1 hour)
 * @param documentTypes - Array of document types to refresh
 */
export function startPeriodicCacheRefresh<TYPE extends NoSqlDoc>(
  documentTypes: FBDocTypeEssentials<TYPE>[],
  intervalMs: number = 24 * 60 * 60 * 1000 // 1 day default
): void {
  // Clear any existing interval
  if (cacheRefreshInterval) {
    clearInterval(cacheRefreshInterval);
  }
  
  // console.log(`Starting periodic cache refresh every ${Math.round(intervalMs / 1000 / 60)} minutes for ${documentTypes.length} document types`);
  
  cacheRefreshInterval = setInterval(async () => {
    // console.log('Performing periodic index cache refresh...');
    
    for (const docType of documentTypes) {
      try {
        await refreshIndexCache(docType);
        // console.log(`Refreshed cache for ${docType.collectionName}`);
      } catch (error) {
        console.error(`Failed to refresh cache for ${docType.collectionName}:`, error);
      }
    }
    
    // console.log('Periodic cache refresh completed');
  }, intervalMs);
}

/**
 * Stops periodic cache refresh
 */
export function stopPeriodicCacheRefresh(): void {
  if (cacheRefreshInterval) {
    clearInterval(cacheRefreshInterval);
    cacheRefreshInterval = null;
    // console.log('Stopped periodic cache refresh');
  }
}

/**
 * Gets cache statistics for monitoring and debugging
 * @returns Object with cache statistics
 */
export function getCacheStats(): {
  entriesCount: number;
  oldestEntry: { baseIndex: string; age: number } | null;
  newestEntry: { baseIndex: string; age: number } | null;
  expiredEntries: string[];
} {
  const now = Date.now();
  const entries = Array.from(indexCache.entries());
  
  if (entries.length === 0) {
    return {
      entriesCount: 0,
      oldestEntry: null,
      newestEntry: null,
      expiredEntries: []
    };
  }
  
  const ages = entries.map(([baseIndex, entry]) => ({
    baseIndex,
    age: now - entry.lastUpdated,
    expired: entry.expiresAt < now
  }));
  
  const oldestEntry = ages.reduce((oldest, current) => 
    current.age > oldest.age ? current : oldest
  );
  
  const newestEntry = ages.reduce((newest, current) =>
    current.age < newest.age ? current : newest
  );
  
  const expiredEntries = ages
    .filter(entry => entry.expired)
    .map(entry => entry.baseIndex);
  
  return {
    entriesCount: entries.length,
    oldestEntry: { baseIndex: oldestEntry.baseIndex, age: oldestEntry.age },
    newestEntry: { baseIndex: newestEntry.baseIndex, age: newestEntry.age },
    expiredEntries
  };
}
